import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import { loadHostGroups } from '@api/filters';
import * as styles from './HostGroupFilter.module.css';

/**
 * HostGroupFilter Component
 * Uses EntityPanelSelector in TWO_COLUMNS mode (no filter dropdown)
 */
function HostGroupFilter({
  selectedHostGroupIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [availableHostGroups, setAvailableHostGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load host groups
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const hostGroups = await loadHostGroups();
        setAvailableHostGroups(hostGroups);
      } catch (error) {
        console.error('Error loading host groups:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Labels for EntityPanelSelector
  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: t('reports.filters.noHostGroupsAvailable'),
    noItemsFound: t('reports.filters.noHostGroupsFound'),
    noItemsSelected: t('reports.filters.noEntitiesSelected'),
  }), [t]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <EntityPanelSelector
      mode={LAYOUT_MODES.TWO_COLUMNS}
      availableItems={availableHostGroups}
      selectedItemIds={selectedHostGroupIds}
      onSelectionChange={onSelectionChange}
      labels={labels}
      valueField="id"
    />
  );
}

HostGroupFilter.propTypes = {
  selectedHostGroupIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export { HostGroupFilter };
export default HostGroupFilter;
